import { ArpMedia, dedupe } from '@arpeggi/shared'
import debounce from 'lodash.debounce'
import { useEffect, useMemo, useState } from 'react'
import { GetArpMediaParams } from '../../../__legacy__/api/v2/ArpMediaAPI'
import { useStatus } from '../../../__legacy__/components/core/Utils'
import { useArpMedias } from '../../../__legacy__/queries/arpMedia'
import { ArpTableContextData } from '../components'
import { ArpMediaFilters, useFilters } from './useFilters'

interface ArpPaginatedData {
  media: ArpMedia[]
  meta: {
    currentPage: number
    lastPage: number
  }
}

const DEFAULT_DATA: ArpPaginatedData = { media: [], meta: { currentPage: 0, lastPage: 1 } }

const LIMIT_PER_PAGE = 30

export const DEFAULT_GET_ARP_MEDIA_PARAMS: GetArpMediaParams = {
  orderBy: 'mintedAt',
  order: 'desc',
  parts: ['subComponents', 'favoriteCount'],
  perPage: LIMIT_PER_PAGE,
  isBlocklist: false,
  shadowBlocklist: true
}

const DEBOUNCE_BY = 500

const arpFiltersToParams = (filters: ArpMediaFilters): GetArpMediaParams => {
  const { typesAndSubTypes, ...rest } = filters
  const type = dedupe(typesAndSubTypes.flatMap((typeAndSubType) => typeAndSubType.types))
  const subType = dedupe(typesAndSubTypes.flatMap((typeAndSubType) => typeAndSubType.subTypes))
  return { ...DEFAULT_GET_ARP_MEDIA_PARAMS, ...rest, type, subType }
}

const useDebounced = <T extends (...args: any) => any>(func: T) =>
  useMemo(() => debounce(func, DEBOUNCE_BY), [])

const useArpData = (filters: ArpMediaFilters) => {
  const [filterStatus, setStatus] = useStatus('loading')
  const handleFilters = (_filters: ArpMediaFilters) => {
    console.log('updating filters')
    setParams(arpFiltersToParams(_filters))
    setStatus('ready')
  }
  const debouncedUpdate = useDebounced(handleFilters)
  const [params, setParams] = useState<GetArpMediaParams>(arpFiltersToParams(filters))

  useEffect(() => {
    setStatus('loading')
    debouncedUpdate(filters)
    console.log('[useArpData] triggered useEffect on filters', filters)
  }, [filters])

  const { fetchNextPage, hasNextPage, data: _data, status: _status } = useArpMedias(params)
  const data = _data?.pages.flatMap((page) => page.data) || []
  const status = _status === 'loading' ? 'loading' : filterStatus

  return { hasMore: hasNextPage, data, status, nextPage: fetchNextPage }
}

export const useArpTable = (defaultFilters: ArpMediaFilters): ArpTableContextData => {
  const { filters, updateFilters } = useFilters(defaultFilters)
  const { hasMore, data, status, nextPage } = useArpData(filters)
  return { data, hasMore, status, nextPage, updateFilters, filters, defaultFilters }
}
